/**
 * Which fields an audit covers, and how they are split across screens.
 *
 * Pure so it can be tested without rendering the modal.
 */

import { FieldsConfig } from '../../../services/configService';
import { Session, isValidationEdit } from '../../../store/sessionSlice';
import { getFieldDefinition, getFieldGroup } from '../../../utils/fieldUtils';

/** The fields a validator reviewed — the audit's work list, in edit-history order. */
export const auditableFieldNames = (session: Session): string[] => {
  const seen = new Set<string>();
  for (const entry of session.edit_history || []) {
    if (isValidationEdit(entry)) seen.add(entry.field_name);
  }
  return Array.from(seen);
};

/**
 * The work list split into screens, keeping hierarchy relatives together the way validation
 * does — a child like detailed_body_part_symptoms can't be judged without its parent's value
 * in view. Grouping comes from getFieldGroup (the config's depends_on/hierarchy_mapping), the
 * same source the evidence modal uses, so it tracks profile changes instead of a hardcoded list.
 *
 * Only validated fields appear: a group member nobody validated is not the auditor's business.
 */
export const auditSteps = (session: Session, fieldsConfig: FieldsConfig | null): string[][] => {
  const auditable = auditableFieldNames(session).filter((field) =>
    getFieldDefinition(fieldsConfig, field),
  );
  const auditableSet = new Set(auditable);
  const assigned = new Set<string>();
  const steps: string[][] = [];

  for (const field of auditable) {
    if (assigned.has(field)) continue;
    const group = getFieldGroup(fieldsConfig, field).filter((member) => auditableSet.has(member));
    // getFieldGroup returns the hierarchy root first; fall back to the field itself if the
    // config has no group for it.
    const step = group.length > 0 ? group : [field];
    step.forEach((member) => assigned.add(member));
    steps.push(step);
  }
  return steps;
};
